/**
 * PORT: what a mobile shell (src/client/platform: the touch controls of a battle, the Android back button) asks the
 * game. Reads of the game's state and the game's own interface actions only: no platform code here, and nothing the
 * keyboard or the mouse could not do (the touch controls feed the same key events, the back button presses the
 * screen's own close button like a tap on it), so the battle simulation, its recording and online play stay the same.
 */

import * as TT from './touch-texts';
import { betOf } from '../ui/wide-fingers';

/**
 * What the on-screen battle controls offer now, read from the game's own state (the keys and clicks the game takes in
 * that state): only the controls that do something are shown (touch-controls.ts), and a hint says what a mode wants.
 */
export interface BattleControls {
  /**
   * 'turn': the worm walks, jumps, aims and fires; 'retreat': after the shot it may still walk and jump; 'target': the
   * selected weapon waits for a point of the map (strikes, teleports, girders: a double click there, Escape leaves);
   * 'worm': the worm choice utility (Enter: the next worm, Escape: this one); 'fly': a jet pack or an open parachute
   * (the arrows steer, Space ends it); 'burst': a burst of bullets (up / down aim); 'tool': a blow torch or a drill
   * (Space stops it, the torch's up / down tilt it); 'busy': an action that takes no input (a blow, a teleport on its
   * way, the pause between the shots of a multi-shot weapon); 'menu': the weapon sheet or the scanner is open.
   */
  mode: 'turn' | 'retreat' | 'target' | 'worm' | 'fly' | 'burst' | 'tool' | 'busy' | 'menu';
  /** Left / right: walk, steer (jet pack, parachute), the strike's side, turn the girder, the next worm; null: unused. */
  sides: 'walk' | 'steer' | 'strike' | 'girder' | 'worm' | null;
  /** Up / down: the selected weapon's sight (only weapons that have one), steering; null: unused. */
  vertical: 'aim' | 'steer' | null;
  /** The forward jump (Enter) and the back-flip (Backspace). */
  jumps: boolean;
  /** Where the worm looks (vars.direction 1 = left): its forward jump goes there, its back-flip the other way. */
  facing: 'left' | 'right';
  /**
   * Space: 'charge' (held: the shot's power), 'shoot' (a press), 'use' (a utility: a medikit, low gravity, skipping
   * the turn...: nothing is fired), 'stop' (ends a jet pack, a parachute, a tool), 'detonate' (a sheep or an old woman
   * on its way), 'confirm' (a target mode: the target is confirmed by a double click on the map, not by Space; the worm
   * choice: Escape); null: nothing to fire.
   */
  fire: 'charge' | 'shoot' | 'use' | 'stop' | 'detonate' | 'confirm' | null;
  /** What a target mode waits for: a strike's target, a teleport's destination, a girder's place. */
  target: 'strike' | 'teleport' | 'girder' | null;
  /** A strike's side (proc.strike_dir: its projectiles come from the left or from the right). */
  strikeFrom: 'left' | 'right' | null;
  /** Escape leaves the target mode (a girder pack: ends it after the girders set so far). */
  cancel: boolean;
  /** The fuse of the selected weapon in seconds when the digit keys 1-5 set it, else null. */
  fuse: number | null;
  /** The bounce of the selected weapon when '+' (max) / '-' (min) set it, else null. */
  bounce: 'max' | 'min' | null;
  /** The weapon sheet opens now (Q). */
  weapons: boolean;
  /** An open menu in an online battle, whose turn clock does not stop: the seconds left of the turn; else null. */
  clock: number | null;
  /** What the player is asked to do: lines of Russian source texts (touch-texts.ts), translated by the shell. */
  hint: string[];
  /** A target mode's hint once a point of the map is chosen (one line: it may go to the top, over the map). */
  hintReady: string[];
}

/** Specials of a worm (obj.states.special) that are animations of the idle worm, not actions: they take the keys. */
const IDLE_SPECIALS = new Set(['', 'WP OUT', 'WP IN', 'CROSS TO LAST', 'LANDING']);

/** The screen stack of the interface has this screen open. */
function screenOpen(fwp: any, name: string): boolean {
  const screens = fwp && fwp._interf && fwp._interf.cur_screen;
  const top = screens ? Number(screens[0]) : 0;
  for (let i = 1; i <= top; i++) if (screens[i] && screens[i].name === name) return true;
  return false;
}

/**
 * A utility of the weapon sheet (its type 'u'): Space uses it (a medikit, low gravity, invisibility, more time, skipping
 * the turn, a jet pack, a tool, the worm choice...) instead of firing something; not the utilities that are thrown (the
 * gravity grenade) or aimed at a point of the map (the teleports, the girders: target modes).
 */
function isUtility(flags: any): boolean {
  return flags.wptype === 'u' && !flags.MAKEPROJECTILE && !flags.MOUSECONTROL;
}

/** A sheep or an old woman of this battle is on its way (CONTROLEXPLODE: Space explodes it). */
function detonatable(fwp: any, p: any): boolean {
  const slots = p.OBJ;
  const n = slots ? Number(slots[0]) : 0;
  for (let i = 1; i <= n; i++) {
    const s = slots[i];
    if (!s || s.free) continue;
    const o = fwp._obj && fwp._obj[s.fid];
    if (o && o.FLAGS && o.FLAGS.CONTROLEXPLODE && !o.death) return true;
  }
  return false;
}

/**
 * The on-screen controls for the running battle, or null when there are none: no battle, a replay, a battle replayed
 * from a command log, not the local player's turn (the opponent, the AI, between the turns).
 */
export function battleControls(fwp: any): BattleControls | null {
  const p = fwp && fwp._proc;
  if (!p || fwp.main_mode !== 'LOCAL_GAME' || fwp.REPLAY) return null;
  const input = p.battle_input;
  if (!input || !input.interactive) return null;
  if (!p.game_control || !p.cur_worm) return null;
  const worm = p.worms && p.worms[p.cur_worm];
  if (!worm || worm.state !== 'ok') return null;
  const obj = fwp._obj && fwp._obj[worm.fid];
  if (!obj || obj.ai) return null;
  const st = obj.states || {};
  const vars = obj.vars || {};
  const flags = obj.weapon_flags || {};
  const c: BattleControls = {
    mode: 'turn',
    sides: null,
    vertical: null,
    jumps: false,
    facing: vars.direction === 1 ? 'left' : 'right',
    fire: null,
    target: null,
    strikeFrom: null,
    cancel: false,
    fuse: null,
    bounce: null,
    weapons: false,
    clock: null,
    hint: [],
    hintReady: [],
  };
  if (p.wpsheet || screenOpen(fwp, 'scaner')) {
    c.mode = 'menu';
    const freezes = typeof p.menus_freeze === 'function' ? p.menus_freeze() : true;
    if (!freezes && typeof vars.timeout === 'number' && vars.timeout !== -100) c.clock = Math.max(0, Math.ceil(vars.timeout / 1000));
    if (p.wpsheet) c.hint = [TT.TT_SHEET];
    return c;
  }
  if (p.GAME_FREEZE) return null;
  if (p.choose_worm) {
    c.mode = 'worm';
    c.sides = 'worm';
    c.fire = 'confirm';
    c.hint = [TT.TT_WORM_CHOICE];
    return c;
  }
  if (p.mousecontrol) {
    c.mode = 'target';
    c.fire = 'confirm';
    c.cancel = true;
    if (flags.SPECIAL === 'air_strike') {
      c.target = 'strike';
      c.sides = 'strike';
      c.strikeFrom = p.strike_dir === 'from right' ? 'right' : 'left';
      c.hint = [TT.TT_TARGET_PICK, TT.TT_STRIKE_SIDE];
      c.hintReady = [TT.TT_TARGET_READY];
    } else if (flags.SPECIAL === 'girder') {
      c.target = 'girder';
      c.sides = 'girder';
      c.hint = [TT.TT_PLACE_PICK, TT.TT_GIRDER_TURN];
      c.hintReady = [TT.TT_TARGET_READY];
    } else {
      c.target = 'teleport';
      c.hint = [TT.TT_PLACE_PICK];
      c.hintReady = [TT.TT_TARGET_READY];
    }
    return c;
  }
  const special = String(st.special ?? '');
  if (special === 'jet_pack' || special === 'parachute') {
    c.mode = 'fly';
    c.sides = 'steer';
    c.vertical = 'steer';
    c.fire = 'stop';
    c.hint = [special === 'jet_pack' ? TT.TT_JET_PACK : TT.TT_PARACHUTE];
    return c;
  }
  if (special === 'bullets') {
    c.mode = 'burst';
    c.vertical = 'aim';
    return c;
  }
  if (special === 'blow_torch' || special === 'pneumatic_drill') {
    c.mode = 'tool';
    c.vertical = special === 'blow_torch' ? 'aim' : null;
    c.fire = 'stop';
    c.hint = [TT.TT_TOOL_STOP];
    return c;
  }
  if (!IDLE_SPECIALS.has(special)) {
    c.mode = 'busy';
    return c;
  }
  if (!st.firecontrol) {
    // After the shot (TAKEMOVE: a few seconds to walk away), or between the shots of a multi-shot weapon.
    if (!st.escape) {
      c.mode = 'busy';
      return c;
    }
    c.mode = 'retreat';
    c.sides = 'walk';
    c.jumps = !vars.paralyzed;
    if (detonatable(fwp, p)) {
      c.fire = 'detonate';
      c.hint = [TT.TT_DETONATE];
    }
    return c;
  }
  c.sides = 'walk';
  c.jumps = !vars.paralyzed;
  const weapon = obj.weapon || {};
  c.weapons = !weapon.shots_total;
  // A weapon being put away (WP IN: Escape left a target mode) is gone at the end of its animation.
  if (obj.weapon_id && special !== 'WP IN') {
    c.vertical = flags.ALLOWCROSSHAIR ? 'aim' : null;
    c.fire = flags.FORCE ? 'charge' : isUtility(flags) ? 'use' : 'shoot';
    if (flags.ALLOWSETTIME) c.fuse = Math.min(5, Math.max(1, Math.round(Number(flags.w_seconds) / 1000) || 3));
    if (flags.ALLOWSETBOUNCE) c.bounce = Number(flags.w_bounce) >= 0.4 ? 'max' : 'min';
    if (flags.ONFALLINGONLY && !st.flying) c.hint = [TT.TT_PARACHUTE_JUMP];
    else if (c.fire === 'use') c.hint = [flags.wid === 'skip_go' ? TT.TT_USE_SKIP : TT.TT_USE];
  }
  return c;
}

/** A battle's local player controls a worm now, with no menu open (the touch controls' battle buttons are wanted). */
export function battleControlsActive(fwp: any): boolean {
  const c = battleControls(fwp);
  return !!c && c.mode !== 'menu';
}

/** Instance name of the battle screen's surrender button (new_interf 'game': proc.cancel_fight). */
export const SURRENDER_BUTTON_NAME = 'cancel_fight';

/**
 * A tap on `target` gives up the running battle: the battle screen's surrender button (not a replay's, where the same
 * button only leaves the replay). A touch screen asks first (a finger near the weapon sheet's icon may hit it).
 */
export function surrenderAt(fwp: any, target: any): boolean {
  if (!fwp || fwp.main_mode !== 'LOCAL_GAME' || fwp.REPLAY) return false;
  for (let o = target; o; o = o.parent) if (o.name === SURRENDER_BUTTON_NAME) return true;
  return false;
}

/**
 * A tap on `target` places a diamond bet of the roulette (its text field, or the row's pad: ui/wide-fingers.ts) that
 * the player can pay: one tap spends real-money diamonds at once, so a touch screen asks first, with the bet's own
 * words ("Поставити 25 алмазів?"). Null for anything else (a gold bet, a bet the player cannot pay: the game does
 * nothing then).
 */
export function diamondBetAt(fwp: any, target: any): { question: string } | null {
  const i = fwp && fwp._interf;
  const top = i && i.cur_screen ? i.cur_screen[i.cur_screen[0]] : null;
  if (!top || top.name !== 'roul') return null;
  const bet = betOf(target);
  if (!bet || bet.currency !== 'diam' || !bet.field) return null;
  if (!(Number(fwp.player?.diam?.value) >= bet.amount)) return null;
  const text = String(bet.field.text ?? '').replace(/\s+/g, ' ').trim();
  return text ? { question: `${text}?` } : null;
}

/**
 * Holds a solo battle still while the touch screen's question before surrender is open (proc.GAME_FREEZE, as the
 * weapon sheet holds it: its turn clock does not run while the player reads); an online battle runs on
 * (proc.menus_freeze false: the other players' ticks go on). Returns what lets the battle go on (once; nothing when it
 * did not hold it).
 */
export function holdBattle(fwp: any): () => void {
  const p = fwp && fwp._proc;
  if (!p || fwp.main_mode !== 'LOCAL_GAME' || fwp.REPLAY || p.GAME_FREEZE || typeof p.menus_freeze !== 'function' || !p.menus_freeze()) return () => undefined;
  p.GAME_FREEZE = true;
  let held = true;
  return () => {
    if (!held) return;
    held = false;
    if (fwp._proc === p && p.GAME_FREEZE === true) p.GAME_FREEZE = false;
  };
}

/**
 * The battle screen's HUD icons (new_interf 'game'): the scanner, the weapon sheet, surrender; the original's at its
 * lower right, a wide screen's under the timer (ui/wide-screen.ts, which hides the weapon sheet's in touch mode).
 */
export const BATTLE_HUD_NAMES: readonly string[] = ['open_scaner', 'open_wpsheet', SURRENDER_BUTTON_NAME];

/**
 * Stage rectangles of the battle HUD's icons shown now (none outside a battle): what the touch controls must not
 * cover where they sit over the window (a tablet).
 */
export function battleHudBounds(fwp: any): Array<{ x: number; y: number; width: number; height: number }> {
  const screens = fwp && fwp.main_mode === 'LOCAL_GAME' && fwp._interf && fwp._interf.cur_screen;
  const out: Array<{ x: number; y: number; width: number; height: number }> = [];
  if (!screens) return out;
  for (let i = 1; i <= Number(screens[0]); i++) {
    const link = screens[i] && screens[i].name === 'game' ? screens[i].link : null;
    if (!link || !link.stage) continue;
    for (const name of BATTLE_HUD_NAMES) {
      const o = link[name];
      if (!o || !o.stage || !shownCentre(o)) continue;
      const b = o.getBounds(o.stage);
      out.push({ x: b.x, y: b.y, width: b.width, height: b.height });
    }
  }
  return out;
}

/** The battle menus' content the touch controls' menu info keeps clear of: the weapon sheet's cells, the scanner's list. */
export const BATTLE_MENU_CONTENT: Readonly<Record<string, string>> = { weapon_sheet: 'in_game_weapon_container', scaner: 'scaner_container' };

/**
 * Stage rectangles of the open battle menus' content (the weapon sheet's block of cells, the scanner's list; within the
 * game's window: a scrolled list reaches its edges), none while no menu is open: what the touch controls' menu info
 * (its hint, an online battle's clock) must not cover where it can help it.
 */
export function battleMenuBounds(fwp: any): Array<{ x: number; y: number; width: number; height: number }> {
  const screens = fwp && fwp.main_mode === 'LOCAL_GAME' && fwp._interf && fwp._interf.cur_screen;
  const out: Array<{ x: number; y: number; width: number; height: number }> = [];
  if (!screens) return out;
  const W = Number(fwp.WINDOW_WIDTH) || 807;
  const H = Number(fwp.WINDOW_HEIGHT) || 600;
  for (let i = 1; i <= Number(screens[0]); i++) {
    const name = screens[i] && BATTLE_MENU_CONTENT[screens[i].name];
    const o = name && screens[i].link ? screens[i].link[name] : null;
    if (!o || !o.stage || !o.visible || !(o.numChildren > 0)) continue;
    const b = o.getBounds(o.stage);
    const x = Math.max(0, b.x);
    const y = Math.max(0, b.y);
    const w = Math.min(W, b.x + b.width) - x;
    const h = Math.min(H, b.y + b.height) - y;
    if (w > 0 && h > 0) out.push({ x, y, width: w, height: h });
  }
  return out;
}

/**
 * Close buttons of the game's screens and popups, in the order they are tried when several are on the stage (the top
 * one wins anyway). Not the battle's surrender button (cancel_fight): back never gives up a battle.
 */
export const CLOSE_BUTTON_NAMES: readonly string[] = ['close_wpsheet', 'close_scaner', 'close_tutorial', 'close_present', 'close_win_game', 'close', 'close_to_mf', 'close_to_assault', 'cancel'];

/** Instance name of the battle screen's map: an invisible button over the whole battle window (new_interf 'game'). */
export const BATTLE_FIELD_NAME = 'inv_button';

/**
 * The battle's map is what a finger at stage point (x, y) hit (`target`: the player's pick there): its taps (a
 * target's double tap, holding the camera) stay exact, without the touch aids of the menus (no target slop, no long
 * press). A miss inside the shown map's bounds counts as the map too; the HUD, the weapon sheet and popups drawn
 * over it keep the aids.
 */
export function battleFieldAt(fwp: any, target: any, x: number, y: number): boolean {
  for (let o = target; o; o = o.parent) if (o.name === BATTLE_FIELD_NAME) return true;
  if (target) return false;
  const screens = fwp && fwp._interf && fwp._interf.cur_screen;
  const top = screens ? Number(screens[0]) : 0;
  for (let i = 1; i <= top; i++) {
    const s = screens[i];
    const map = s && s.name === 'game' && s.link ? s.link[BATTLE_FIELD_NAME] : null;
    if (!map || !map.stage || !shownCentre(map)) continue;
    const b = map.getBounds(map.stage);
    if (x >= b.x && x <= b.x + b.width && y >= b.y && y <= b.y + b.height) return true;
  }
  return false;
}

/** Stage point of a display object's centre, or null when it is not shown. */
function shownCentre(o: any): { x: number; y: number } | null {
  for (let a = o; a; a = a.parent) if (!a.visible || a.alpha === 0) return null;
  if (!o.stage) return null;
  const b = o.getBounds(o.stage);
  if (!(b.width > 0 && b.height > 0)) return null;
  return { x: b.x + b.width / 2, y: b.y + b.height / 2 };
}

function isOrContains(parent: any, child: any): boolean {
  for (let c = child; c; c = c.parent) if (c === parent) return true;
  return false;
}

/**
 * The close button a tap would reach now: the topmost shown display object with a close name (CLOSE_BUTTON_NAMES)
 * whose centre is not covered by anything else that takes the mouse; null when the screen has none (the main map).
 */
export function findCloseButton(stage: any): { target: any; x: number; y: number } | null {
  if (!stage) return null;
  const found: any[] = [];
  const walk = (o: any) => {
    if (!o || !o.visible) return;
    const n = typeof o.numChildren === 'number' ? o.numChildren : 0;
    // Topmost first: the last child is drawn over the others.
    for (let i = n - 1; i >= 0; i--) walk(o.getChildAt(i));
    if (o !== stage && CLOSE_BUTTON_NAMES.includes(o.name) && o.mouseEnabled !== false) found.push(o);
  };
  walk(stage);
  for (const target of found) {
    const c = shownCentre(target);
    if (!c) continue;
    const hit = typeof stage.$mousePick === 'function' ? stage.$mousePick(Math.round(c.x * 20), Math.round(c.y * 20)) : null;
    if (hit && hit.kind === 'hit' && !isOrContains(target, hit.target)) continue;
    return { target, x: c.x, y: c.y };
  }
  return null;
}

/** What the back button did: 'handled', or 'exit' (nothing to go back to: the shell asks whether to leave the game). */
export type BackResult = 'handled' | 'exit';

export interface BackInput {
  /** A tap at a stage point (through the player's mouse input, like a finger). */
  tap(x: number, y: number): void;
  /** A key press (through the player's keyboard input). */
  key(keyCode: number): void;
}

/**
 * The Android back button: like the game's own close buttons. The sign-in screen goes back to its first view; a popup
 * or a screen closes through its close button; in a battle without one, Escape (the keyboard's key: it cancels a
 * target pick); otherwise, with nothing to close, the shell asks whether to exit: on the main map, and while the game
 * waits for the server (NET_INIT: a backend call pending or retried with a countdown while the server does not
 * answer, which may last as long as the server is away) or is still loading.
 */
export function backAction(fwp: any, input: BackInput): BackResult {
  if (!fwp || !fwp.stage) return 'exit';
  if (fwp.auth_screen) return fwp.auth_screen.back() ? 'handled' : 'exit';
  // The chat panel over the main map: back leaves a question or the hidden players, else closes it.
  if (fwp.chat_ui) return fwp.chat_ui.back() ? 'handled' : 'exit';
  // The account's e-mail dialog and the delete-account dialog close (not while their request is in flight).
  if (fwp.email_dialog) return fwp.email_dialog.back() ? 'handled' : 'exit';
  if (fwp.delete_dialog) return fwp.delete_dialog.back() ? 'handled' : 'exit';
  // The settings screen's account dialog closes.
  if (fwp.account_dialog) return fwp.account_dialog.back() ? 'handled' : 'exit';
  // The store's purchase dialog closes (not while the purchase is running).
  if (fwp.store_dialog) return fwp.store_dialog.back() ? 'handled' : 'exit';
  if (fwp.animation) return 'handled'; // a screen is sliding in or out
  const close = findCloseButton(fwp.stage);
  if (close) {
    input.tap(close.x, close.y);
    return 'handled';
  }
  if (fwp.main_mode === 'LOCAL_GAME') {
    input.key(27);
    return 'handled';
  }
  return 'exit';
}
